import { isPositiveIntegerText } from "./count-input.js";
import { environmentObservationValues } from "./environment-report-flow.js";
import { validateArizonaPostalCode } from "./arizona-postal-code.js";

export function buildCommunityReportPayload({
  state,
  categories,
  submittedAt,
  idFactory,
  reportCoordinates = null,
  reportLocationH3 = null,
}) {
  const payload = {
    id: idFactory(),
    category: categories,
    zip_code: normalizedArizonaPostalCode(primaryZipCode(state, categories)),
    submitted_at: submittedAt,
  };
  const hasExactLocationCategory = categories.some((category) => category === "animals" || category === "environment");
  const isHumanOnly = categories.includes("people") && !hasExactLocationCategory;
  if (categories.includes("people") && hasExactLocationCategory) {
    throw new Error("mixed people and exact-location payloads must be split before payload construction");
  }
  if (isHumanOnly && typeof reportLocationH3 === "string" && reportLocationH3.trim()) {
    payload.location_h3 = reportLocationH3.trim();
  }
  const coordinates = normalizedReportCoordinates(reportCoordinates);
  if (coordinates && hasExactLocationCategory) {
    payload.latitude = coordinates.latitude;
    payload.longitude = coordinates.longitude;
  }

  if (categories.includes("people")) {
    payload.feeling = state.feeling;
    if (state.feeling === "sick") {
      payload.symptoms = state.symptoms.map((item) => item.toLowerCase());
      payload.other_symptom_text = state.symptoms.includes("Other") ? state.otherSymptom || null : null;
      const sickCount = optionalCountValue(state.sickCount);
      if (sickCount !== null) payload.people_sick_count = sickCount;
      payload.symptom_start = normalizeReportDate(state.onset);
      payload.professionally_diagnosed = state.diagnosed === "Yes" ? true : state.diagnosed === "No" ? false : null;
      payload.diagnosis = state.diagnosed === "Yes" ? state.diagnosis || null : null;
      const calledInSick = calledInSickPayloadValue(state);
      if (calledInSick) payload.called_in_sick_school_work = calledInSick;
      const exposures = exposurePayloadValue(state.exposures);
      if (exposures) payload.exposures = exposures;
    }
  }

  if (categories.includes("animals")) {
    payload.species_text = state.animalSpecies.trim();
    payload.animal_type = state.animalType;
    payload.animal_group = state.animalGroup;
    if (state.animalStatus) payload.animal_status = state.animalStatus;
    payload.animal_observations = animalObservationValues(state);
    if (state.animalStatus === "sick") {
      const sickCount = optionalCountValue(state.animalSickCount);
      if (sickCount !== null) payload.sick_count = sickCount;
    }
    if (state.animalStatus === "dead") {
      const deadCount = optionalCountValue(state.animalDeadCount);
      if (deadCount !== null) payload.dead_count = deadCount;
    }
    const animalObservedDate = normalizeReportDate(state.animalObservedDate);
    if (animalObservedDate) payload.animal_observed_date = animalObservedDate;
    if (state.animalZip) payload.animal_zip_code = normalizedArizonaPostalCode(state.animalZip);
    if (state.animalNotes) payload.animal_notes = state.animalNotes;
  }

  if (categories.includes("environment")) {
    payload.environment_issue_type = state.environmentIssueType;
    if (state.environmentIssueType === "insect_vectors") payload.insect_vector_type = state.insectVectorType;
    if (state.environmentIssueType === "environmental_concern") payload.environment_concerns = [...state.environmentConcerns];
    payload.environment_observations = environmentObservationValues(state);
    const environmentObservedDate = normalizeReportDate(state.environmentObservedDate);
    if (environmentObservedDate) payload.environment_observed_date = environmentObservedDate;
    if (state.environmentZip) {
      payload.environment_zip_code = normalizedArizonaPostalCode(state.environmentZip);
    }
    if (state.environmentNotes) payload.environment_notes = state.environmentNotes;
  }

  const observations = combinedObservations(state, categories);
  if (observations.length > 0) payload.observations = observations;

  const notes = combinedNotes(state, categories);
  if (notes) payload.notes = notes;

  return payload;
}

function normalizedArizonaPostalCode(value) {
  const validation = validateArizonaPostalCode(value);
  if (!validation.ok) throw new Error("An Arizona ZIP code or ZIP+4 is required");
  return validation.value;
}


export function normalizedReportCoordinates(coordinates) {
  if (
    coordinates &&
    typeof coordinates.latitude === "number" &&
    typeof coordinates.longitude === "number" &&
    Number.isFinite(coordinates.latitude) &&
    Number.isFinite(coordinates.longitude) &&
    coordinates.latitude >= -90 &&
    coordinates.latitude <= 90 &&
    coordinates.longitude >= -180 &&
    coordinates.longitude <= 180
  ) {
    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    };
  }
  return null;
}

function primaryZipCode(state, categories) {
  if (state.feeling === "sick" && categories.includes("people")) return state.zip;
  if (categories.includes("people") && state.zip) return state.zip;
  if (categories.includes("animals") && state.animalZip) return state.animalZip;
  if (categories.includes("environment") && state.environmentZip) return state.environmentZip;
  return state.zip || state.animalZip || state.environmentZip;
}

function combinedObservations(state, categories) {
  const observations = [];
  if (categories.includes("animals")) observations.push(...animalObservationValues(state));
  if (categories.includes("environment")) observations.push(...environmentObservationValues(state));
  return observations;
}

function combinedNotes(state, categories) {
  const notes = [];
  if (categories.includes("animals") && state.animalNotes.trim()) {
    notes.push(`Animals: ${state.animalNotes.trim()}`);
  }
  if (categories.includes("environment") && state.environmentNotes.trim()) {
    notes.push(`Environment: ${state.environmentNotes.trim()}`);
  }
  return notes.length > 0 ? notes.join("\n\n") : null;
}

function normalizeReportDate(value) {
  const clean = String(value || "").trim();
  if (!clean) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return clean;
  const legacyBuckets = {
    today: "today",
    yesterday: "yesterday",
    "this week": "this_week",
    "last week": "last_week",
    unknown: "unknown",
  };
  const normalized = clean.toLowerCase();
  return legacyBuckets[normalized] || normalized.replace(/\s+/g, "_");
}

function calledInSickPayloadValue(state) {
  if (state.calledInSick === "Yes") return "yes";
  if (state.calledInSick === "No") return "no";
  if (state.calledInSick === "N/A") return "not_applicable";
  return null;
}

function exposurePayloadValue(exposures) {
  if (!exposures) return null;

  const payload = {
    mass_gathering: { occurred: yesNoBoolean(exposures.massGathering) },
    live_animal_contact: { occurred: yesNoBoolean(exposures.liveAnimalContact) },
    dead_animal_contact: { occurred: yesNoBoolean(exposures.deadAnimalContact) },
    sick_individual_contact: { occurred: yesNoBoolean(exposures.sickIndividualContact) },
    tick_or_insect_bite: { occurred: yesNoBoolean(exposures.tickOrInsectBite) },
    recent_travel: { occurred: yesNoBoolean(exposures.recentTravel) },
  };

  if (payload.mass_gathering.occurred) {
    payload.mass_gathering.event_text = stringOrNull(exposures.massGatheringEvent);
  }
  if (payload.live_animal_contact.occurred) {
    payload.live_animal_contact.bitten = yesNoBoolean(exposures.liveAnimalBitten);
    payload.live_animal_contact.animal_appeared_sick = yesNoBoolean(exposures.liveAnimalAppearedSick);
  }
  if (payload.recent_travel.occurred) {
    payload.recent_travel.location_text = stringOrNull(exposures.recentTravelLocation);
  }

  return Object.values(payload).some((exposure) => exposure.occurred !== null) ? payload : null;
}

function yesNoBoolean(value) {
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
}

function stringOrNull(value) {
  const clean = String(value || "").trim();
  return clean || null;
}

function animalObservationValues(state) {
  const structuredValues = [state.animalType, state.animalGroup].filter(Boolean);
  return structuredValues.length > 0 ? structuredValues : [...state.animalObservations];
}

function optionalCountValue(value) {
  return isPositiveIntegerText(value) ? Number(value) : null;
}
